import {
	Box,
	Button,
	HStack,
	IconButton,
	Table,
	TableContainer,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tooltip,
	Tr
} from "@chakra-ui/react";
import { cloneDeep } from "lodash";
import { CSSProperties, FunctionComponent, ReactElement, useEffect, useMemo, useState } from "react";
import { MdDelete, MdInfoOutline } from "react-icons/md";

// Components
import HighFixedValueCaseStatus from "../../../components/claims/HighFixedValueCaseStatus";
import DetailField from "../../../shared/DetailElement/DetailField";
import StepperFooter from "../../../shared/StickyFooter/footers/StepperFooter";

// Hooks
import { useAuth } from "../../../hooks/useAuth";

// Utils
import currency from "currency.js";
import { validateClaimsBilling } from "../../../utils/api/claims";
import { BillingType, EnvKeys, UserRole } from "../../../utils/consts";
import { getEnvValue } from "../../../utils/helpers";

// Types
import { Claim, ClaimService, ClaimServiceBillingType, LegalBenefit, User } from "../../../utils/types";

// Context
import { useClaim } from "../../../context-providers/claims-provider";
import CustomModal from "../../../shared/Modals/CustomModal";

interface BillingProps {
	/**
	 * Delegate to handle logic when User clicks the Previous button
	 * @returns void
	 */
	handleStepPrevious: () => void;
	handleStepClick: (index: number) => void;
	currentStep: number;
	/** If the component is in admin mode, where services & billing are edited together */
	isInAdminMode?: boolean;
}

export interface ExtendedLegalBenefit extends LegalBenefit {
	// Injected fields
	hasHourlyRateOverride: boolean;
}

const Billing: FunctionComponent<BillingProps> = (props: BillingProps): ReactElement => {
	const { handleStepPrevious, handleStepClick, currentStep, isInAdminMode } = props;

	const formatDecimal = (val: string) => {
		return val !== "0" && val.length ? val : "";
	};

	const formatMoney = (val: string) => {
		return val !== "0" && val.length ? `$` + val : "";
	};

	const {
		localClaim,
		localBenefits,
		billHourlyAsFlat,
		billFlatAsHourly,
		setLocalClaim,
		setBillHourlyAsFlat,
		setBillFlatAsHourly,
		handleClaimUpsert
	} = useClaim();
	const { getUser } = useAuth();
	const currentUser: User | undefined = getUser();

	const [totalHours, setTotalHours] = useState<string>("");
	const [totalNetworkCost, setTotalNetworkCost] = useState<string>("");
	const [totalStandardCost, setTotalStandardCost] = useState<string>("");
	const [confirmHourlyFlatFeeOpen, setConfirmHourlyFlatFeeOpen] = useState<boolean>(false);
	const [confirmFlatFeeHourlyOpen, setConfirmFlatFeeHourlyOpen] = useState<boolean>(false);

	const networkRate = formatDecimal(getEnvValue(EnvKeys.NetworkRate));
	const networkRateParalegal = formatDecimal(getEnvValue(EnvKeys.NetworkRateParalegal));

	const getLegalBenefit = (id: number): ExtendedLegalBenefit | undefined => {
		const foundBenefit = localBenefits.find((benefitItem: LegalBenefit) => benefitItem.id === id);
		if (!foundBenefit) return;

		// If the benefit has an hourly rate greater than the default network rate, count it as an override
		return {
			...foundBenefit,
			hasHourlyRateOverride:
				!!foundBenefit.hourlyRateCap &&
				!!networkRate &&
				parseFloat(foundBenefit.hourlyRateCap) > parseFloat(networkRate)
		};
	};

	// Only managers can exceed billing limits, if there is no LAMP case
	const canExceedBillingLimit = useMemo(() => {
		let isClaimsManager: boolean = false;
		currentUser?.role.split(",").forEach((role: string) => {
			if (role === UserRole.ClaimsManager) {
				isClaimsManager = true;
			}
		});
		return isClaimsManager && (localClaim.lampCaseId === undefined || localClaim.lampCaseId === 0);
	}, [currentUser, localClaim]);

	// Identify which services are High Fixed Value benefits
	const serviceTypes = useMemo(() => {
		return localClaim.claimServices.map((service) => {
			const benefit = service.legalBenefitId ? getLegalBenefit(service.legalBenefitId) : undefined;

			// Determine if this is a high fixed value benefit (either by billing type or specific test ID)
			const isHighFixedValueByType = benefit?.billingType === BillingType.FixedHighValue;
			const isHighFixedValue = isHighFixedValueByType;

			return {
				isHighFixedValue: isHighFixedValue,
				isHourly: benefit?.billingType === BillingType.Hourly
			};
		});
	}, [localClaim.claimServices, localBenefits]);

	// Handle case status updates for High Fixed Value benefits
	const updateCaseStatus = (serviceIndex: number, field: string, value: boolean) => {
		setLocalClaim({
			...localClaim,
			claimServices: localClaim.claimServices.map((s, sIndex) => {
				if (sIndex === serviceIndex) {
					// If field is finalDisposition and value is true,
					// reset all other flags since we're marking as 100% complete
					if (field === "finalDisposition" && value === true) {
						return {
							...s,
							finalDisposition: true,
							consultationCompleted: false,
							officeWorkCompleted: false,
							negotiationCorrespondenceCompleted: false,
							lawsuitCaseFiledRespondedCompleted: false,
							courtAppearanceCompleted: false,
							caseFullyCompleted: false
						};
					}

					// Update individual fields
					return {
						...s,
						[field]: value
					};
				}
				return s;
			})
		});
	};

	// Calculate High Fixed Value percentage for a service
	const calculateHighFixedValuePercentage = (service: ClaimService): number => {
		if (service.finalDisposition) {
			return 100;
		}

		let highestPercentage = 0;

		if (service.consultationCompleted) highestPercentage = Math.max(highestPercentage, 5);
		if (service.officeWorkCompleted) highestPercentage = Math.max(highestPercentage, 15);
		if (service.negotiationCorrespondenceCompleted) highestPercentage = Math.max(highestPercentage, 25);
		if (service.lawsuitCaseFiledRespondedCompleted) highestPercentage = Math.max(highestPercentage, 50);
		if (service.courtAppearanceCompleted) highestPercentage = Math.max(highestPercentage, 80);
		if (service.caseFullyCompleted) highestPercentage = Math.max(highestPercentage, 100);

		return highestPercentage;
	};

	// Check if any High Fixed Value services are missing final disposition selection
	const hasUnselectedHighFixedValue = useMemo(() => {
		return localClaim.claimServices.some((service, index) => {
			if (serviceTypes[index]?.isHighFixedValue) {
				return service.finalDisposition === undefined;
			}
			return false;
		});
	}, [localClaim.claimServices, serviceTypes]);

	const validationError = useMemo(() => {
		// Check if any High Fixed Value services have 0% completion
		for (const [index, service] of localClaim.claimServices.entries()) {
			const percentage = calculateHighFixedValuePercentage(service) / 100;

			if (serviceTypes[index]?.isHighFixedValue && percentage === 0) {
				return "High Fixed Value services must have at least one case work item completed";
			}

			const benefit = service.legalBenefitId ? getLegalBenefit(service.legalBenefitId) : undefined;
			const networkMax = parseFloat(benefit?.totalAmountCap || "0");
			const networkMaxTotal = (networkMax * percentage).toFixed(2);
			const serviceTotalAmount = service.billing?.reduce((acc, i) => acc + parseFloat(i.totalAmount ?? "0"), 0) ?? 0;

			if (
				!canExceedBillingLimit &&
				serviceTypes[index]?.isHighFixedValue &&
				!billFlatAsHourly &&
				serviceTotalAmount > parseFloat(networkMaxTotal)
			) {
				return "Combined charges cannot exceed the service max amount of $" + networkMaxTotal;
			}
		}

		if (currentUser) return validateClaimsBilling(localClaim, localBenefits, currentUser, billHourlyAsFlat);
	}, [localClaim, localBenefits, serviceTypes, billHourlyAsFlat, currentUser, canExceedBillingLimit]);

	/**
	 * Handles the logic when the "Next" button is clicked
	 * Upserts Claim and proceeds to next step
	 */
	const handleNextClick = async () => {
		try {
			const workingCopyClone = cloneDeep(localClaim);
			// Inject totals so server can validate them
			let isSuccess = await handleClaimUpsert(workingCopyClone, true);
			if (isSuccess) {
				// Proceed to next step
				handleStepClick(currentStep + 1);
			}
		} catch (e) {
			console.log("error in next click", e);
		}
	};

	/**
	 * Handles the logic when the "Previous" button is clicked
	 * saves the current working copy and decrements the current step
	 * Calls delegate to decrement current step
	 * @returns void
	 */
	const handlePreviousClick = async (): Promise<void> => {
		try {
			const workingCopyClone = cloneDeep(localClaim);
			setLocalClaim(workingCopyClone);
			handleStepPrevious();
		} catch (e) {
			console.error("error in previous click", e);
		}
	};

	const onDeleteRow = async (serviceIndex: number, chargeIndex: number) => {
		setLocalClaim({
			...localClaim,
			claimServices: localClaim.claimServices.map((s, sIndex) => {
				if (sIndex === serviceIndex)
					return {
						...s,
						billing: s.billing?.filter((_, cIndex) => cIndex !== chargeIndex)
					};
				return s;
			})
		});
	};

	const onAddRow = (serviceIndex: number) => {
		const benefit = localClaim.claimServices?.[serviceIndex]?.legalBenefitId
			? getLegalBenefit(localClaim.claimServices[serviceIndex].legalBenefitId as number)
			: undefined;
		if (!benefit) {
			return;
		}
		const isHourly = benefit.billingType === BillingType.Hourly || billFlatAsHourly;
		const isHighFixedValue = serviceTypes[serviceIndex]?.isHighFixedValue;

		const billingType = isHourly
			? ClaimServiceBillingType.HourlyAttorney
			: isHighFixedValue
				? ClaimServiceBillingType.FlatHigh
				: ClaimServiceBillingType.Flat;

		if (isHighFixedValue) {
			console.log(`Adding High Fixed Value Billing Row - Service ${serviceIndex}:`, {
				benefitId: localClaim.claimServices[serviceIndex].legalBenefitId,
				benefitTotalAmountCap: benefit.totalAmountCap,
				discountedRate: benefit.totalAmountCap
			});
		}

		setLocalClaim({
			...localClaim,
			claimServices: localClaim.claimServices.map((s, sIndex) => {
				if (sIndex === serviceIndex)
					return {
						...s,
						billing: s.billing?.length
							? s.billing?.concat({
									claimId: localClaim.claimId!,
									type: billingType,
									discountedRate:
										(isHourly && !billHourlyAsFlat) || billFlatAsHourly
											? benefit.hasHourlyRateOverride
												? benefit.hourlyRateCap
												: networkRate
											: benefit.totalAmountCap,
									standardRate:
										(isHourly && !billHourlyAsFlat) || billFlatAsHourly
											? benefit.providerStandardHourlyRate ?? ""
											: benefit.providerStandardFlatRate ?? "",
									hours: (isHourly && !billHourlyAsFlat) || billFlatAsHourly ? "" : undefined,
									serviceId: s.serviceId!
								})
							: [
									{
										claimId: localClaim.claimId!,
										type: billingType,
										discountedRate:
											(isHourly && !billHourlyAsFlat) || billFlatAsHourly
												? benefit.hasHourlyRateOverride
													? benefit.hourlyRateCap
													: networkRate
												: benefit.totalAmountCap,
										standardRate:
											(isHourly && !billHourlyAsFlat) || billFlatAsHourly
												? benefit.providerStandardHourlyRate ?? ""
												: benefit.providerStandardFlatRate ?? "",
										hours: (isHourly && !billHourlyAsFlat) || billFlatAsHourly ? "" : undefined,
										serviceId: s.serviceId!
									}
								]
					};
				return s;
			})
		});
	};

	const onBillingProviderTypeChange = (serviceIndex: number, chargeIndex: number, value: string) => {
		setLocalClaim({
			...localClaim,
			claimServices: localClaim.claimServices.map((s, sIndex) => {
				if (sIndex === serviceIndex) {
					const benefit = s.legalBenefitId ? getLegalBenefit(s.legalBenefitId as number) : undefined;
					const benefitHourlyRate =
						benefit && benefit.hasHourlyRateOverride && benefit.hourlyRateCap ? benefit.hourlyRateCap : networkRate;
					return {
						...s,
						billing: s.billing?.map((c, cIndex) => {
							if (cIndex === chargeIndex) {
								return {
									...c,
									type:
										value === "attorney"
											? ClaimServiceBillingType.HourlyAttorney
											: ClaimServiceBillingType.HourlyParalegal,
									discountedRate: value === "attorney" ? benefitHourlyRate : networkRateParalegal
								};
							}
							return c;
						})
					};
				}
				return s;
			})
		});
	};

	const onHoursChange = (serviceIndex: number, chargeIndex: number, value: string) => {
		setLocalClaim({
			...localClaim,
			claimServices: localClaim.claimServices.map((s, sIndex) => {
				if (sIndex === serviceIndex)
					return {
						...s,
						billing: s.billing?.map((c, cIndex) => {
							if (cIndex === chargeIndex) {
								return { ...c, hours: value };
							}
							return c;
						})
					};
				return s;
			})
		});
	};

	const onTotalChange = (serviceIndex: number, chargeIndex: number, value: string) => {
		setLocalClaim({
			...localClaim,
			claimServices: localClaim.claimServices.map((s, sIndex) => {
				if (sIndex === serviceIndex)
					return {
						...s,
						billing: s.billing?.map((c, cIndex) => {
							if (cIndex === chargeIndex) {
								return { ...c, totalAmount: value };
							}
							return c;
						})
					};
				return s;
			})
		});
	};

	const onStandardChange = (serviceIndex: number, chargeIndex: number, value: string) => {
		setLocalClaim({
			...localClaim,
			claimServices: localClaim.claimServices.map((s, sIndex) => {
				if (sIndex === serviceIndex)
					return {
						...s,
						billing: s.billing?.map((c, cIndex) => {
							if (cIndex === chargeIndex) return { ...c, standardRate: value };
							return c;
						})
					};
				return s;
			})
		});
	};

	const onOtherNonCoveredFees = (value: string) => {
		const workingCopyClone: Claim = cloneDeep(localClaim);
		workingCopyClone.otherNonCoveredFees = value;
		setLocalClaim(workingCopyClone);
	};

	const onOtherNonCoveredServicesHours = (value: string) => {
		const workingCopyClone: Claim = cloneDeep(localClaim);
		workingCopyClone.otherNonCoveredHours = value;
		setLocalClaim(workingCopyClone);
	};

	/** Initial list of services and if any are hourly */
	const [serviceOptions, hasHourlyService, hasHighFixedValueService] = useMemo(() => {
		let hasHourly = false;
		let hasHighFixedValue = false;
		const options = localClaim.claimServices.map((item, index) => {
			const benefit = item.legalBenefitId ? getLegalBenefit(item.legalBenefitId) : undefined;
			const isHourly = benefit?.billingType === BillingType.Hourly;
			const isHighFixedValue = serviceTypes[index]?.isHighFixedValue;
			if (isHourly) {
				hasHourly = true;
			}
			if (isHighFixedValue) {
				hasHighFixedValue = true;
			}
			// Detect when a benefit was converted to flat rate
			if (
				isHourly &&
				item.billing?.some((billing) => billing.type === ClaimServiceBillingType.Flat) &&
				!billHourlyAsFlat
			) {
				setBillHourlyAsFlat(true);
				setBillFlatAsHourly(false);
			}
			// Detect when a benefit was converted from high fixed value to hourly rate
			if (
				isHighFixedValue &&
				item.billing?.some(
					(billing) =>
						billing.type &&
						[ClaimServiceBillingType.HourlyAttorney, ClaimServiceBillingType.HourlyParalegal].includes(billing.type)
				) &&
				!billFlatAsHourly
			) {
				setBillHourlyAsFlat(false);
				setBillFlatAsHourly(true);
			}
			// Service id should always be defined before getting to this step
			return benefit
				? `${benefit.benefitTitle} ${benefit.matterBillingCode} for ${item.serviceProvidedFor ?? ""}${item.recipientAge != null ? " (Age " + item.recipientAge + ")" : ""}`
				: "";
		});
		return [options, hasHourly, hasHighFixedValue];
	}, [localClaim, localBenefits, serviceTypes]);

	/** Update local claim and recalculate total costs when charges change */
	useEffect(() => {
		const workingCopyClone = cloneDeep(localClaim);

		let hoursTotal = currency(0);
		let networkCostTotal = currency(0);
		let standardCostTotal = 0;
		let isBillingUpdated = false;
		workingCopyClone.claimServices
			.flatMap((service, serviceIndex) => {
				// Initialize all services to have at least one charge, if not in admin mode
				if (service.billing?.length) {
					return service.billing;
				} else {
					const benefit = service.legalBenefitId ? getLegalBenefit(service.legalBenefitId) : undefined;
					if (!benefit) {
						return [];
					}
					const isHourly = benefit.billingType === BillingType.Hourly || billFlatAsHourly;
					const isHighFixedValue = serviceTypes[serviceIndex]?.isHighFixedValue && !billFlatAsHourly;

					// Only inject billing if not in admin mode, or admin mode for an non-hourly benefit
					if (
						!isInAdminMode ||
						(isInAdminMode && (!isHourly || (isHourly && (billHourlyAsFlat || !billFlatAsHourly))))
					) {
						isBillingUpdated = true;

						service.billing = [
							{
								type: isHourly
									? ClaimServiceBillingType.HourlyAttorney
									: isHighFixedValue
										? ClaimServiceBillingType.FlatHigh
										: ClaimServiceBillingType.Flat,
								discountedRate:
									isHourly && !billHourlyAsFlat
										? benefit.hasHourlyRateOverride
											? benefit.hourlyRateCap
											: networkRate
										: benefit.totalAmountCap,
								standardRate:
									isHourly && !billHourlyAsFlat ? benefit.providerStandardHourlyRate : benefit.providerStandardFlatRate,
								hours: isHourly && !billHourlyAsFlat ? "" : undefined,
								totalAmount: undefined,
								id: undefined,
								serviceId: service.serviceId
							}
						];
						return service.billing;
					} else {
						return [];
					}
				}
			})
			.forEach((charge) => {
				if (charge) {
					if (
						charge.type === ClaimServiceBillingType.Flat ||
						charge.type === ClaimServiceBillingType.FlatHigh ||
						billHourlyAsFlat
					) {
						if (charge.totalAmount) {
							networkCostTotal = currency(networkCostTotal).add(currency(charge.totalAmount || "0"));
						}
						if (charge.standardRate) {
							standardCostTotal = standardCostTotal + parseFloat(charge.standardRate);
						}
					}
					if (
						charge.type !== ClaimServiceBillingType.Flat &&
						charge.type !== ClaimServiceBillingType.FlatHigh &&
						!billHourlyAsFlat
					) {
						if (charge.hours) {
							hoursTotal = currency(hoursTotal).add(parseFloat(parseFloat(charge.hours).toFixed(2)));
						}
						if (charge.totalAmount) {
							networkCostTotal = currency(networkCostTotal).add(currency(charge.totalAmount));
						}
						if (charge.standardRate && charge.hours) {
							standardCostTotal =
								standardCostTotal + parseFloat(charge.standardRate) * parseFloat(parseFloat(charge.hours).toFixed(2));
						}
					}
				}
			});
		setTotalHours(hoursTotal.toString());
		setTotalNetworkCost(networkCostTotal.format({ symbol: "" }));
		// Round to currency only after calculations
		setTotalStandardCost(currency(standardCostTotal).format({ symbol: "" }));
		if (isBillingUpdated) {
			setLocalClaim(workingCopyClone);
		}
	}, [localClaim, localBenefits, serviceTypes]);

	/** Recalculate total sums when totals change */
	useEffect(() => {
		let claimPlusNonCoveredItemsTotal = currency(totalNetworkCost ?? 0);
		let marketValueTotal = currency(totalStandardCost ?? 0);

		let nonCoveredCosts = currency(0);
		if (localClaim.otherNonCoveredFees) {
			nonCoveredCosts = currency(nonCoveredCosts).add(localClaim.otherNonCoveredFees);
		}
		if (localClaim.otherNonCoveredHours) {
			nonCoveredCosts = currency(nonCoveredCosts).add(localClaim.otherNonCoveredHours);
		}

		claimPlusNonCoveredItemsTotal = currency(claimPlusNonCoveredItemsTotal).add(nonCoveredCosts);
		marketValueTotal = currency(marketValueTotal).add(nonCoveredCosts);

		// If local claim is not updated, update there as well
		if (
			localClaim.billingtotal !== marketValueTotal.format({ separator: "", symbol: "" }) ||
			localClaim.billingAdditionalFees !== claimPlusNonCoveredItemsTotal.format({ separator: "", symbol: "" })
		) {
			setLocalClaim({
				...localClaim,
				billingtotal: marketValueTotal.value.toFixed(2),
				billingAdditionalFees: claimPlusNonCoveredItemsTotal.value.toFixed(2)
			});
		}
	}, [localClaim, totalNetworkCost, totalStandardCost]);

	const handleConvertHourlyToFlatFee = () => {
		setLocalClaim({
			...localClaim,
			claimServices: localClaim.claimServices.map((s, sIndex) => {
				return {
					...s,
					billing: []
				};
			})
		});
		// If originally high fixed value, set back to high fixed value
		if (billFlatAsHourly) {
			setBillFlatAsHourly(false);
		} else {
			// Otherwise set hourly to flat
			setBillHourlyAsFlat(true);
		}
		setConfirmHourlyFlatFeeOpen(false);
	};

	const handleConvertFlatToHourlyFee = () => {
		setLocalClaim({
			...localClaim,
			claimServices: localClaim.claimServices.map((s, sIndex) => {
				return {
					...s,
					billing: []
				};
			})
		});
		// If originally hourly, set back to hourly
		if (billHourlyAsFlat) {
			setBillHourlyAsFlat(false);
		} else {
			// Otherwise set high fixed value to hourly
			setBillFlatAsHourly(true);
		}
		setConfirmFlatFeeHourlyOpen(false);
	};

	return (
		<Box>
			{/* Render High Fixed Value Case Status sections */}
			{localClaim.claimServices.map((service, serviceIndex) =>
				serviceTypes[serviceIndex]?.isHighFixedValue ? (
					<HighFixedValueCaseStatus
						key={`high-fixed-value-${serviceIndex}`}
						service={service}
						serviceIndex={serviceIndex}
						updateCaseStatus={updateCaseStatus}
					/>
				) : null
			)}

			<CustomModal
				isOpen={confirmHourlyFlatFeeOpen}
				size="xl"
				title="Confirm Billing Flat Fee"
				message="This will clear your hourly fees and allow a single flat fee. Continue?"
				continueText="Continue"
				onContinue={() => handleConvertHourlyToFlatFee()}
				onCancel={() => setConfirmHourlyFlatFeeOpen(false)}
			/>
			<CustomModal
				isOpen={confirmFlatFeeHourlyOpen}
				size="xl"
				title="Confirm Billing Hourly"
				message="This will clear your flat fee and allow hourly fees. Continue?"
				continueText="Continue"
				onContinue={() => handleConvertFlatToHourlyFee()}
				onCancel={() => setConfirmFlatFeeHourlyOpen(false)}
			/>

			{/* Only show billing content when all High Fixed Value services have made a selection */}
			{!hasUnselectedHighFixedValue && (
				<>
					<TableContainer overflowX="auto">
						<Table variant="unstyled" size="xs">
							<Thead backgroundColor="gray.300">
								<Tr h="44px">
									<Th
										style={styles.tableHeaderCell}
										borderLeftRadius="4px"
										pl={2}
										maxW={{ base: "150px", md: "200px" }}>
										Benefit Charges
									</Th>
									{((hasHourlyService && !billHourlyAsFlat) || billFlatAsHourly) && (
										<>
											<Th w="83px" style={styles.tableHeaderCell} maxW={{ base: "100px", md: "150px" }}>
												Provider Type
											</Th>
											<Th style={styles.tableHeaderCell} maxW={{ base: "80px", md: "120px" }}>
												Hours
											</Th>
										</>
									)}
									<Th maxW={{ base: "100px", md: "150px" }} style={styles.tableHeaderCell}>
										{(hasHourlyService && !billHourlyAsFlat) || billFlatAsHourly ? "Network Rate" : "Network Maximum"}
									</Th>
									<Th maxW={{ base: "100px", md: "150px" }} style={styles.tableHeaderCell}>
										{(hasHourlyService && !billHourlyAsFlat) || billFlatAsHourly
											? "Standard Rate"
											: "Standard Flat Fee"}
										&nbsp;
										<Tooltip
											label={
												((hasHourlyService && !billHourlyAsFlat) || billFlatAsHourly
													? "The standard hourly rate"
													: "The standard flat fee") + " that would be charged to a client with no Legal Plan."
											}
											placement="bottom">
											<span>
												<MdInfoOutline style={{ display: "inline-block", fontSize: "16px", verticalAlign: "bottom" }} />
											</span>
										</Tooltip>
									</Th>
									<Th style={styles.tableHeaderCell} maxW={{ base: "80px", md: "120px" }}>
										Total
									</Th>
									<Th style={styles.tableHeaderCell} p="0px !important" borderRightRadius="4px"></Th>
								</Tr>
							</Thead>
							<Tbody>
								{localClaim.claimServices.map((service, serviceIndex) => {
									const serviceBenefit = service.legalBenefitId ? getLegalBenefit(service.legalBenefitId) : undefined;
									const serviceTotalAmount =
										service.billing?.reduce((acc, i) => acc + parseFloat(i.totalAmount ?? "0"), 0) ?? 0;
									return service.billing?.length
										? service.billing.map((charge, chargeIndex, charges) => (
												<>
													<Tr height="38px">
														<Td style={styles.tableBodyCell} pr={3}>
															<Text pl={2} whiteSpace="break-spaces">
																{chargeIndex === 0 && serviceOptions[serviceIndex]}
															</Text>
														</Td>
														{((hasHourlyService && !billHourlyAsFlat) || billFlatAsHourly) && (
															<>
																<Td
																	style={styles.tableBodyCell}
																	pr={3}
																	width={
																		charge.type !== ClaimServiceBillingType.Flat &&
																		(charge.type !== ClaimServiceBillingType.FlatHigh || billFlatAsHourly)
																			? "40px"
																			: "auto"
																	}>
																	{charge.type !== ClaimServiceBillingType.Flat &&
																	(charge.type !== ClaimServiceBillingType.FlatHigh || billFlatAsHourly) ? (
																		<DetailField
																			type="select"
																			fontSize="1rem"
																			variant="flushed"
																			minWidth="96px"
																			marginBottom="0px"
																			borderRadius="0"
																			isDeleteable={true}
																			isEdit={true}
																			displayValue={""}
																			value={
																				charge.type === ClaimServiceBillingType.HourlyAttorney
																					? "attorney"
																					: "paralegal"
																			}
																			onTextChange={(event) =>
																				onBillingProviderTypeChange(serviceIndex, chargeIndex, event.target.value)
																			}
																			values={{ attorney: "Attorney", paralegal: "Paralegal" }}
																			allowNew={false}
																		/>
																	) : (
																		"N/A"
																	)}
																</Td>
																<Td
																	style={styles.tableBodyCell}
																	pr={3}
																	width={
																		charge.type !== ClaimServiceBillingType.Flat &&
																		(charge.type !== ClaimServiceBillingType.FlatHigh || billFlatAsHourly)
																			? "40px"
																			: "auto"
																	}>
																	{charge.type !== ClaimServiceBillingType.Flat &&
																	(charge.type !== ClaimServiceBillingType.FlatHigh || billFlatAsHourly) ? (
																		<DetailField
																			displayValue="Hours"
																			isEdit={true}
																			variant="flushed"
																			type="decimal"
																			fontSize="1rem"
																			w="80px"
																			value={formatDecimal(charge.hours ?? "")}
																			onDecimalChange={(v) => onHoursChange(serviceIndex, chargeIndex, v)}
																			precision={2}
																		/>
																	) : (
																		"N/A"
																	)}
																</Td>
															</>
														)}
														<Td style={styles.tableBodyCell}>
															<DetailField
																displayValue={
																	serviceTypes[serviceIndex]?.isHighFixedValue
																		? "Network Maximum"
																		: "Network Hourly Rate"
																}
																isEdit={true}
																display="inline-block"
																variant="flushed"
																type="decimal"
																fontSize="1rem"
																maxW="100px"
																minW="80px"
																value={
																	serviceTypes[serviceIndex]?.isHighFixedValue && !billFlatAsHourly
																		? formatMoney(
																				(
																					(parseFloat(charge.discountedRate ?? "0") *
																						calculateHighFixedValuePercentage(localClaim.claimServices[serviceIndex])) /
																					100
																				).toFixed(2)
																			)
																		: formatMoney(charge.discountedRate ?? "")
																}
																precision={2}
																isDisabled={true}
															/>
															{serviceTypes[serviceIndex]?.isHighFixedValue && !billFlatAsHourly && (
																<Tooltip
																	label={`The highest level of work done on this case was "${(() => {
																		const service = localClaim.claimServices[serviceIndex];
																		if (service.caseFullyCompleted) return "Case Completed";
																		if (service.courtAppearanceCompleted) return "Court Appearance(s)";
																		if (service.lawsuitCaseFiledRespondedCompleted)
																			return "Lawsuit/Case Filed or Responded To";
																		if (service.negotiationCorrespondenceCompleted) return "Negotiation/Correspondence";
																		if (service.officeWorkCompleted) return "Office Work";
																		if (service.consultationCompleted) return "Consultation";
																		return "None";
																	})()}". The maximum payout for this level of work is the Network Maximum × Case work percentage = Total. $${parseFloat(charge.discountedRate ?? "0").toFixed(0)} × ${calculateHighFixedValuePercentage(localClaim.claimServices[serviceIndex])}% = $${((parseFloat(charge.discountedRate ?? "0") * calculateHighFixedValuePercentage(localClaim.claimServices[serviceIndex])) / 100).toFixed(2)}.`}
																	placement="top"
																	hasArrow
																	bg="gray.700"
																	color="white">
																	<Box display="inline-flex" alignItems="center" verticalAlign="middle">
																		<MdInfoOutline style={{ fontSize: "16px", cursor: "pointer" }} />
																	</Box>
																</Tooltip>
															)}
														</Td>
														<Td style={styles.tableBodyCell}>
															<DetailField
																displayValue={
																	serviceTypes[serviceIndex]?.isHighFixedValue
																		? "Standard Flat Fee"
																		: "Standard Hourly Rate"
																}
																isEdit={true}
																variant="flushed"
																type="decimal"
																fontSize="1rem"
																maxW="100px"
																minW="80px"
																value={formatMoney(charge.standardRate ?? "")}
																onDecimalChange={(v) => onStandardChange(serviceIndex, chargeIndex, v)}
																precision={2}
															/>
														</Td>
														<>
															<Td style={styles.tableBodyCell}>
																<DetailField
																	displayValue="Total Amount"
																	isEdit={true}
																	variant="flushed"
																	type="decimal"
																	fontSize="1rem"
																	maxW="100px"
																	minW="80px"
																	value={formatMoney(charge.totalAmount ?? "")}
																	onDecimalChange={(v) => onTotalChange(serviceIndex, chargeIndex, v)}
																	error={
																		!canExceedBillingLimit &&
																		serviceBenefit?.totalAmountCap &&
																		serviceTotalAmount > parseFloat(serviceBenefit.totalAmountCap)
																			? `Exceeds service's max combined amount of $${serviceBenefit.totalAmountCap}, so will require approval`
																			: undefined
																	}
																	precision={2}
																/>
															</Td>
															<Td style={styles.tableBodyCell} p="0px !important">
																{(chargeIndex !== 0 || isInAdminMode) && (
																	<IconButton
																		minW="32px"
																		minH="32px"
																		height="32px"
																		colorScheme="blackAlpha"
																		variant="ghost"
																		aria-label="Remove Charge"
																		onClick={() => onDeleteRow(serviceIndex, chargeIndex)}
																		icon={<MdDelete />}
																	/>
																)}
															</Td>
														</>
													</Tr>
													{/* For hourly benefits, allow adding charges and converting to flat fee */}
													{charge.type &&
														[ClaimServiceBillingType.HourlyAttorney, ClaimServiceBillingType.HourlyParalegal].includes(
															charge.type
														) &&
														chargeIndex === charges.length - 1 && (
															<Tr>
																<Td colSpan={hasHourlyService || hasHighFixedValueService ? 6 : 3}>
																	<HStack justifyContent="end" mt={5} mb={5} gap={4}>
																		{!billHourlyAsFlat && (
																			<>
																				<Button
																					colorScheme="blackAlpha"
																					size="sm"
																					onClick={() => onAddRow(serviceIndex)}>
																					Add Charge
																				</Button>
																				<Button
																					colorScheme="blackAlpha"
																					size="sm"
																					onClick={() => setConfirmHourlyFlatFeeOpen(true)}>
																					Bill Flat Fee Instead
																				</Button>
																			</>
																		)}
																		{billHourlyAsFlat && (
																			<Button
																				colorScheme="blackAlpha"
																				size="sm"
																				onClick={() => setConfirmFlatFeeHourlyOpen(true)}>
																				Bill Hourly Instead
																			</Button>
																		)}
																	</HStack>
																</Td>
															</Tr>
														)}
													{/* For high fixed value benefits, allow conversion to hourly */}
													{charge.type &&
														charge.type == ClaimServiceBillingType.FlatHigh &&
														chargeIndex === charges.length - 1 && (
															<Tr>
																<Td colSpan={hasHourlyService || hasHighFixedValueService ? 6 : 3}>
																	<HStack justifyContent="end" mt={5} mb={5} gap={4}>
																		{billFlatAsHourly && (
																			<>
																				<Button
																					colorScheme="blackAlpha"
																					size="sm"
																					onClick={() => onAddRow(serviceIndex)}>
																					Add Charge
																				</Button>
																				<Button
																					colorScheme="blackAlpha"
																					size="sm"
																					onClick={() => setConfirmHourlyFlatFeeOpen(true)}>
																					Bill Flat Fee Instead
																				</Button>
																			</>
																		)}
																		{!billFlatAsHourly && (
																			<Button
																				colorScheme="blackAlpha"
																				size="sm"
																				onClick={() => setConfirmFlatFeeHourlyOpen(true)}>
																				Bill Hourly Instead
																			</Button>
																		)}
																	</HStack>
																</Td>
															</Tr>
														)}
													{/* If the benefit is hourly but the charge is flat, allow converting back to hourly */}
													{serviceBenefit?.billingType === BillingType.Hourly &&
														charge.type &&
														charge.type === ClaimServiceBillingType.Flat &&
														chargeIndex === charges.length - 1 && (
															<Tr>
																<Td colSpan={hasHourlyService || hasHighFixedValueService ? 6 : 3}>
																	<HStack justifyContent="end" mt={5} mb={5} gap={4}>
																		{billHourlyAsFlat && (
																			<Button
																				colorScheme="blackAlpha"
																				size="sm"
																				onClick={() => setConfirmFlatFeeHourlyOpen(true)}>
																				Bill Hourly Instead
																			</Button>
																		)}
																	</HStack>
																</Td>
															</Tr>
														)}
												</>
											))
										: localBenefits.length > serviceIndex &&
												localBenefits[serviceIndex].billingType != null &&
												[BillingType.Hourly, BillingType.FixedHighValue].includes(
													localBenefits[serviceIndex].billingType!
												) && (
													<>
														<Tr>
															<Td style={styles.tableBodyCell} pr={3}>
																<Text pl={2} whiteSpace="break-spaces">
																	{serviceOptions[serviceIndex]}
																</Text>
															</Td>
															<Td colSpan={5}>
																<Text textAlign="right" color="orange.500" fontStyle="italic">
																	Each service must have at least one charge
																</Text>
															</Td>
														</Tr>
														<Tr>
															<Td colSpan={hasHourlyService || hasHighFixedValueService ? 6 : 3}>
																<HStack justifyContent="end" mt={5} mb={5}>
																	<Button colorScheme="blackAlpha" size="sm" onClick={() => onAddRow(serviceIndex)}>
																		Add Charge
																	</Button>
																	<Button
																		colorScheme="blackAlpha"
																		size="sm"
																		onClick={() => setConfirmHourlyFlatFeeOpen(true)}>
																		Bill Flat Fee Instead
																	</Button>
																</HStack>
															</Td>
														</Tr>
													</>
												);
								})}
							</Tbody>
						</Table>
					</TableContainer>

					<HStack
						mt={4}
						spacing={4}
						flexDirection={{ base: "column", md: "row" }}
						alignItems={{ base: "stretch", md: "flex-start" }}
						w="100%">
						<Box flex="1">
							<TableContainer overflowX="auto">
								<Table variant="unstyled" size="xs">
									<Thead>
										<Tr h="44px">
											<Th
												backgroundColor="gray.300"
												style={styles.tableHeaderCell}
												borderLeftRadius="4px"
												borderRightRadius="4px"
												pl={2}>
												Other Charges
											</Th>
											<Th w={3}></Th>
											<Th backgroundColor="gray.300" style={styles.tableHeaderCell} borderRadius="4px" pl={2}>
												Totals
											</Th>
										</Tr>
									</Thead>
									<Tbody>
										<Tr>
											<Td verticalAlign="top">
												<TableContainer>
													<Table variant="unstyled" size="xs">
														<Tbody>
															<Tr>
																<Td style={styles.tableBodyCell} pl={2} pr={3}>
																	Other Non-Covered Charges
																</Td>
																<Td style={styles.tableBodyCell} pr={3}>
																	<DetailField
																		displayValue="Costs"
																		isEdit={true}
																		type="decimal"
																		variant="flushed"
																		fontSize="1rem"
																		maxW="100px"
																		minW="80px"
																		value={formatMoney(localClaim.otherNonCoveredFees ?? "")}
																		onDecimalChange={onOtherNonCoveredFees}
																		precision={2}
																	/>
																</Td>
															</Tr>
															<Tr>
																<Td style={styles.tableBodyCell} pl={2} pr={3} rowSpan={2}>
																	Non-Covered Services/Hours Shown on Invoice
																</Td>
																<Td style={styles.tableTotalValue} pr={3} rowSpan={2}>
																	<DetailField
																		displayValue="Non-covered services/hours shown on invoice"
																		isEdit={true}
																		type="decimal"
																		variant="flushed"
																		fontSize="1rem"
																		maxW="100px"
																		minW="80px"
																		value={formatMoney(localClaim.otherNonCoveredHours ?? "")}
																		onDecimalChange={onOtherNonCoveredServicesHours}
																		precision={2}
																	/>
																</Td>
															</Tr>
														</Tbody>
													</Table>
												</TableContainer>
											</Td>
											<Td></Td>
											<Td verticalAlign="top">
												<TableContainer>
													<Table variant="unstyled" size="xs">
														{hasHourlyService && !billHourlyAsFlat && (
															<Tr>
																<Td style={styles.tableTotalLabel} pl={2} pr={3}>
																	Total Hours
																</Td>
																<Td style={styles.tableTotalValue} pr={3}>
																	<Text fontWeight="500" color="orange.500">
																		{totalHours}
																	</Text>
																</Td>
															</Tr>
														)}
														<Tr>
															<Td style={styles.tableTotalLabel} pl={2} pr={3}>
																Total Standard Cost{" "}
																<Tooltip
																	label="Total attorney and paralegal charges for non-Legal Plan members at standard hourly rates."
																	placement="bottom">
																	<span>
																		<MdInfoOutline
																			style={{
																				display: "inline-block",
																				fontSize: "16px",
																				verticalAlign: "middle",
																				marginBottom: "2px"
																			}}
																		/>
																	</span>
																</Tooltip>
															</Td>
															<Td style={styles.tableTotalValue} pr={3}>
																<Text fontWeight="500" color="orange.500">
																	${totalStandardCost}
																</Text>
															</Td>
														</Tr>
														<Tr>
															<Td style={styles.tableTotalLabel} pl={2} pr={3}>
																Market Value{" "}
																<Tooltip
																	label="Shows the total value of the services if the client did not have a Legal Plan."
																	placement="bottom">
																	<span>
																		<MdInfoOutline
																			style={{
																				display: "inline-block",
																				fontSize: "16px",
																				verticalAlign: "middle",
																				marginBottom: "2px"
																			}}
																		/>
																	</span>
																</Tooltip>
															</Td>
															<Td style={styles.tableTotalValue} pr={3}>
																<Text fontWeight="500" color="orange.500">
																	{currency(localClaim.billingtotal ?? "0").format()}
																</Text>
															</Td>
														</Tr>
														<Tr>
															<Td style={styles.tableTotalLabel} pl={2} pr={3}>
																Total Network Cost
															</Td>
															<Td style={styles.tableTotalValue} pr={3}>
																<Text fontWeight="500" color="orange.500">
																	${totalNetworkCost}
																</Text>
															</Td>
														</Tr>
													</Table>
												</TableContainer>
											</Td>
										</Tr>
									</Tbody>
								</Table>
							</TableContainer>
						</Box>
					</HStack>

					{!isInAdminMode && (
						<Box mt={4}>
							<StepperFooter
								hasPreviousStep={true}
								hasNextStep={true}
								isNextDisabled={!!validationError}
								nextTooltip={validationError}
								isSubmit={false}
								onNext={() => handleNextClick()}
								onPrevious={() => handlePreviousClick()}
							/>
						</Box>
					)}
				</>
			)}
		</Box>
	);
};

const styles: { [id: string]: CSSProperties } = {
	tableHeaderCell: {
		whiteSpace: "break-spaces",
		lineHeight: "1rem",
		textTransform: "none",
		fontSize: "1rem",
		fontWeight: "500",
		paddingRight: "10px",
		paddingTop: "4px",
		paddingBottom: "8px",
		color: "gray.800"
	},
	tableBodyCell: {
		verticalAlign: "bottom",
		whiteSpace: "break-spaces"
	},
	tableTotalLabel: {
		verticalAlign: "middle",
		fontWeight: 500
	},
	tableTotalValue: {
		textAlign: "right"
	}
};

export default Billing;
